import React, { useEffect, useRef, useState, useMemo } from 'react'
import * as d3 from 'd3'
import { graphlib, render as dagreRender } from 'dagre-d3-es'

export function ServiceMap({ data }) {
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    if (!containerRef.current || dimensions.width === 0) return

    const g = new graphlib.Graph({ compound: true, multigraph: true })
      .setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 100, marginx: 20, marginy: 20 })
      .setDefaultEdgeLabel(() => ({}))

    // Add clusters by type
    const types = [...new Set(data.map(n => n.type))]
    types.forEach((type, i) => {
      g.setNode(`cluster_${type}`, {
        label: type,
        clusterLabelPos: 'top',
        style: 'fill: none; stroke: var(--gridline); stroke-width: 1.5px; stroke-dasharray: 6,4; rx: 8; ry: 8;',
        labelStyle: 'font-weight: 600; fill: var(--text-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;',
      })
    })

    // Add nodes
    data.forEach(node => {
      const statusColors = {
        healthy: 'var(--status-good)',
        degraded: 'var(--status-warning)',
        down: 'var(--status-critical)',
      }
      g.setNode(node.id, {
        label: node.name,
        labelStyle: 'font-size: 12px; fill: var(--text-primary); font-weight: 500;',
        style: `fill: var(--surface-1); stroke: ${statusColors[node.status]}; stroke-width: 2px; rx: 6; ry: 6;`,
        shape: 'rect',
        width: 140,
        height: 50,
        class: 'service-node',
      })
      g.setParent(node.id, `cluster_${node.type}`)
    })

    // Add edges (dependencies)
    data.forEach(node => {
      if (node.dependencies) {
        node.dependencies.forEach(depId => {
          if (g.hasNode(depId) && g.hasNode(node.id)) {
            g.setEdge(depId, node.id, {
              curve: d3.curveBasis,
              lineInterpolate: 'basis',
              style: 'stroke: var(--text-muted); stroke-width: 1.5px; fill: none;',
              arrowhead: 'vee',
              arrowheadStyle: 'fill: var(--text-muted); stroke: var(--text-muted);',
            })
          }
        })
      }
    })

    const renderGraph = dagreRender()
    const svg = d3.select(containerRef.current)
    let inner = svg.select('g')
    if (inner.empty()) inner = svg.append('g')

    inner.attr('transform', `translate(40, 20)`)
    renderGraph(inner, g)

    // Add zoom/pan
    const zoom = d3.zoom().scaleExtent([0.3, 3]).on('zoom', (event) => {
      inner.attr('transform', event.transform)
    })
    svg.call(zoom)

    // Fit to view
    const bounds = inner.node().getBBox()
    if (bounds.width > 0 && bounds.height > 0) {
      const fullWidth = dimensions.width - 80
      const fullHeight = dimensions.height - 40
      const scale = Math.min(fullWidth / bounds.width, fullHeight / bounds.height, 1)
      const translateX = (fullWidth - bounds.width * scale) / 2 - bounds.x * scale
      const translateY = (fullHeight - bounds.height * scale) / 2 - bounds.y * scale
      svg.transition().duration(500).call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale))
    }

    // Add tooltips
    inner.selectAll('.service-node').on('mouseover', function(event, d) {
      const nodeData = data.find(n => n.id === d)
      if (nodeData) {
        showTooltip(event, nodeData)
      }
    }).on('mouseout', hideTooltip)
  }, [data, dimensions])

  const [tooltip, setTooltip] = useState(null)

  const showTooltip = (event, node) => {
    setTooltip({
      x: event.clientX + 10,
      y: event.clientY - 10,
      content: (
        <div className="chart-tooltip">
          <div className="tooltip-title">{node.name}</div>
          <div className="tooltip-row">
            <span className="tooltip-label">Type:</span>
            <span className="tooltip-value">{node.type}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Status:</span>
            <span className="tooltip-value" style={{ color: getStatusColor(node.status) }}>
              {node.status}
              <span className="tooltip-color" style={{ background: getStatusColor(node.status) }} />
            </span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Port:</span>
            <span className="tooltip-value">{node.port}</span>
          </div>
          {node.description && (
            <div className="tooltip-row">
              <span className="tooltip-label">Description:</span>
              <span className="tooltip-value">{node.description}</span>
            </div>
          )}
        </div>
      ),
    })
  }

  const hideTooltip = () => setTooltip(null)

  const getStatusColor = (status) => {
    const colors = {
      healthy: 'var(--status-good)',
      degraded: 'var(--status-warning)',
      down: 'var(--status-critical)',
    }
    return colors[status] || 'var(--text-muted)'
  }

  if (dimensions.width === 0) {
    return <div ref={containerRef} className="loading" style={{ height: '500px' }}>Loading service map...</div>
  }

  return (
    <div className="service-map-container" style={{ height: '500px' }}>
      <div className="map-controls">
        <div className="legend">
          {['healthy', 'degraded', 'down'].map(status => (
            <span key={status} className="legend-item">
              <span className="legend-color" style={{ background: getStatusColor(status) }} />
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          ))}
        </div>
        <div className="map-actions">
          <button className="map-btn" onClick={() => window.dispatchEvent(new CustomEvent('map-zoom-in'))}>+</button>
          <button className="map-btn" onClick={() => window.dispatchEvent(new CustomEvent('map-zoom-out'))}>−</button>
          <button className="map-btn" onClick={() => window.dispatchEvent(new CustomEvent('map-reset'))}>⌂</button>
        </div>
      </div>
      <svg ref={containerRef} className="viz-root" width="100%" height="100%" style={{ display: 'block' }} />
      {tooltip && (
        <div className="chart-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.content}
        </div>
      )}
      <style jsx>{`
        .service-map-container {
          position: relative;
          width: 100%;
          height: 100%;
          background: var(--surface-1);
          border-radius: 8px;
          overflow: hidden;
        }
        .map-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          background: var(--surface-1);
        }
        .legend {
          display: flex;
          gap: 16px;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-secondary);
        }
        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 3px;
        }
        .map-actions {
          display: flex;
          gap: 8px;
        }
        .map-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--surface-1);
          color: var(--text-secondary);
          font-size: 16px;
        }
        .map-btn:hover {
          background: var(--page-plane);
          color: var(--text-primary);
        }
        .loading {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
        }
        .viz-root {
          cursor: grab;
        }
        .viz-root:active {
          cursor: grabbing;
        }
        .viz-root .node rect {
          transition: stroke-width 0.2s;
        }
        .viz-root .node:hover rect {
          stroke-width: 3px;
        }
        .viz-root .edgePath path {
          transition: stroke-width 0.2s;
        }
        .viz-root .edgePath path:hover {
          stroke-width: 2.5px;
          stroke: var(--series-1);
        }
      `}</style>
    </div>
  )
}